import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Home, ArrowLeft, Calendar, BookOpen, HeartHandshake, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const NotFound: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        document.title = `404 - ${t('errors.404.badge', 'Seite nicht gefunden')} | Mosaik e.V.`;
    }, [t]);

    const quickLinks = [
        { to: '/veranstaltungen', label: t('events.breadcrumb', 'Veranstaltungen'), icon: Calendar },
        { to: '/kursangebot', label: t('courses.breadcrumb', 'Kursangebot'), icon: BookOpen },
        { to: '/projekte', label: t('projects.breadcrumb', 'Projekte'), icon: HeartHandshake },
        { to: '/kontakt', label: t('common.contact', 'Kontakt'), icon: Mail },
    ];

    return (
        <div className="relative min-h-[75vh] flex items-center justify-center py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Background Decorative Blur Gradients */}
            <div
                aria-hidden="true"
                className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none"
            />
            <div
                aria-hidden="true"
                className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none"
            />

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative z-10 max-w-2xl w-full text-center"
            >
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 mb-6">
                    <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                    {t('errors.404.badge', '404 - Seite nicht gefunden')}
                </div>

                {/* Big 404 Headline */}
                <h1 className="text-7xl sm:text-9xl font-extrabold tracking-tight bg-gradient-to-r from-primary via-[#1a8ad4] to-accent bg-clip-text text-transparent select-none drop-shadow-sm">
                    404
                </h1>

                {/* Title & Description */}
                <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-foreground">
                    {t('errors.404.title', 'Hoppla! Diese Seite existiert leider nicht.')}
                </h2>
                <p className="mt-3 text-base sm:text-lg text-foreground/70 max-w-lg mx-auto leading-relaxed">
                    {t(
                        'errors.404.description',
                        'Die von Ihnen gesuchte Seite wurde möglicherweise verschoben, umbenannt oder existiert nicht mehr.'
                    )}
                </p>

                {/* Primary Action Buttons */}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium shadow-md shadow-primary/25 hover:bg-primary/90 hover:shadow-lg transition-all duration-200"
                    >
                        <Home className="w-4 h-4" />
                        <span>{t('errors.404.back_home', 'Zur Startseite')}</span>
                    </Link>

                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-secondary text-secondary-foreground font-medium border border-border hover:bg-slate-200/70 dark:hover:bg-slate-800 transition-colors duration-200 cursor-pointer"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>{t('errors.404.go_back', 'Zurück')}</span>
                    </button>
                </div>

                {/* Quick Helpful Links */}
                <div className="mt-12 pt-8 border-t border-border">
                    <p className="text-xs uppercase tracking-wider font-semibold text-foreground/50 mb-4">
                        {t('errors.404.quick_links', 'Vielleicht suchen Sie nach:')}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {quickLinks.map((item) => {
                            const IconComponent = item.icon;
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className="group flex flex-col items-center p-3 rounded-xl bg-white dark:bg-slate-900 border border-border hover:border-primary/50 hover:shadow-sm transition-all"
                                >
                                    <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:scale-110 transition-transform mb-1.5">
                                        <IconComponent className="w-4 h-4" />
                                    </div>
                                    <span className="text-xs font-medium text-foreground/80 group-hover:text-primary">
                                        {item.label}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default NotFound;
